import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Eye, Search, Filter } from 'lucide-react';
import { enquiriesAPI } from '@/utils/api';
import { toast } from 'sonner';
import EnquiryDetailModal from '@/components/admin/EnquiryDetailModal';

const Enquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        setLoading(true);
        const params = {};
        if (statusFilter !== 'all') params.status = statusFilter;
        if (serviceFilter !== 'all') params.service_type = serviceFilter;
        const data = await enquiriesAPI.getAll(params);
        if (!cancelled) setEnquiries(data);
      } catch (error) {
        if (!cancelled) toast.error('Failed to load enquiries');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [statusFilter, serviceFilter, refreshTick]);

  const loadEnquiries = () => setRefreshTick((t) => t + 1);

  const handleViewDetails = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setModalOpen(true);
  };

  const handleEnquiryUpdated = () => {
    loadEnquiries();
    setModalOpen(false);
  };

  const filteredEnquiries = enquiries.filter(enquiry => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      enquiry.customer_name?.toLowerCase().includes(search) ||
      enquiry.customer_email?.toLowerCase().includes(search) ||
      enquiry.customer_phone?.includes(search)
    );
  });

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-500',
      contacted: 'bg-yellow-500',
      converted: 'bg-green-500',
      closed: 'bg-gray-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getUrgencyColor = (urgency) => {
    const colors = {
      emergency: 'text-red-500',
      urgent: 'text-orange-500',
      standard: 'text-gray-400'
    };
    return colors[urgency] || 'text-gray-400';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Enquiries</h1>
          <p className="text-gray-400 mt-1">Manage customer enquiries and leads</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-gray-900/50 border-gray-800">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-gray-800 border-gray-700 text-white"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700">
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={serviceFilter} onValueChange={setServiceFilter}>
              <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                <SelectValue placeholder="Filter by service" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700">
                <SelectItem value="all">All Services</SelectItem>
                <SelectItem value="handyman">Handyman</SelectItem>
                <SelectItem value="air_conditioning">Air Conditioning</SelectItem>
                <SelectItem value="refrigeration">Refrigeration</SelectItem>
                <SelectItem value="plumbing">Plumbing</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Enquiries Table */}
      <Card className="bg-gray-900/50 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center justify-between">
            <span>Enquiries ({filteredEnquiries.length})</span>
            <Button
              onClick={loadEnquiries}
              variant="outline"
              size="sm"
              className="border-gray-700 text-gray-300 hover:bg-gray-800"
            >
              Refresh
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading enquiries...</div>
          ) : filteredEnquiries.length === 0 ? (
            <div className="text-center py-8 text-gray-400">No enquiries found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-gray-800">
                  <tr>
                    <th className="text-left text-gray-400 font-medium py-3 px-4">Customer</th>
                    <th className="text-left text-gray-400 font-medium py-3 px-4">Contact</th>
                    <th className="text-left text-gray-400 font-medium py-3 px-4">Service</th>
                    <th className="text-left text-gray-400 font-medium py-3 px-4">Urgency</th>
                    <th className="text-left text-gray-400 font-medium py-3 px-4">Status</th>
                    <th className="text-left text-gray-400 font-medium py-3 px-4">Date</th>
                    <th className="text-right text-gray-400 font-medium py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEnquiries.map((enquiry) => (
                    <tr
                      key={enquiry.enquiry_id}
                      className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors"
                    >
                      <td className="py-4 px-4">
                        <div className="text-white font-medium">{enquiry.customer_name}</div>
                        <div className="text-gray-400 text-sm truncate max-w-xs">
                          {enquiry.issue_description?.substring(0, 50)}...
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="text-gray-300 text-sm">{enquiry.customer_email}</div>
                        <div className="text-gray-400 text-sm">{enquiry.customer_phone}</div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-gray-300 capitalize">
                          {enquiry.service_type?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`capitalize font-medium ${getUrgencyColor(enquiry.urgency)}`}>
                          {enquiry.urgency}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <Badge className={`${getStatusColor(enquiry.status)} text-white`}>
                          {enquiry.status}
                        </Badge>
                      </td>
                      <td className="py-4 px-4 text-gray-400 text-sm">
                        {new Date(enquiry.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-4 text-right">
                        <Button
                          onClick={() => handleViewDetails(enquiry)}
                          size="sm"
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enquiry Detail Modal */}
      {selectedEnquiry && (
        <EnquiryDetailModal
          enquiry={selectedEnquiry}
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onUpdated={handleEnquiryUpdated}
        />
      )}
    </div>
  );
};

export default Enquiries;